import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ActionDetailsDto,
  Procedure,
  ProcedureDetailsDto,
  ProcedureItemsResponse,
  ProcedureItemUpdateResponse,
  ProcedureStatus,
  ProcedureStatusDisplayKey,
  QuickApprovalResponse,
  QuickApprovalScope,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class ProceduresService {
  private readonly baseUrl = environment.baseUrl;

  constructor(private readonly http: HttpClient) {}

  fetchProcedures(): Observable<Procedure[]> {
    return this.http
      .get<{ procedures: Procedure[] }>(`${this.baseUrl}/CMDISOProcedure`)
      .pipe(
        map((data) =>
          data.procedures.map((procedure) => ({
            ...procedure,
          })),
        ),
      );
  }

  fetchProcedureById(procedureId: string): Observable<Procedure> {
    return this.http.get<Procedure>(
      `${this.baseUrl}/CMDISOProcedure/${procedureId}/header`,
    );
  }

  fetchProcedureItems(procedureId: string): Observable<ProcedureItemsResponse> {
    return this.http.get<ProcedureItemsResponse>(
      `${this.baseUrl}/CMDISOProcedure/${procedureId}/items`,
    );
  }

  approveProcedureItemStatus(
    procedureId: string,
    itemId: string,
  ): Observable<ProcedureItemUpdateResponse> {
    return this.http.post<ProcedureItemUpdateResponse>(
      `${this.baseUrl}/CMDISOProcedure/${procedureId}/items/${itemId}/approve`,
      {},
    );
  }

  unApproveProcedureItemStatus(
    procedureId: string,
    itemId: string,
    correlationId: string,
    targetStatus: 'Pending' | 'Skipped',
    rowVersion?: string,
  ): Observable<ProcedureItemUpdateResponse> {
    return this.http.post<ProcedureItemUpdateResponse>(
      `${this.baseUrl}/CMDISOProcedure/${procedureId}/items/${itemId}/unapprove`,
      {
        correlationId,
        targetStatus,
        rowVersion,
      },
    );
  }

  fetchProcedureStatuses(): Observable<ProcedureStatus[]> {
    return this.http.get<ProcedureStatus[]>(
      `${this.baseUrl}/CMDISOProcedure/master/statuses`,
    );
  }

  fetchProcedureItemStatuses(): Observable<ProcedureStatus[]> {
    return this.http.get<ProcedureStatus[]>(
      `${this.baseUrl}/CMDISOProcedure/master/item-statuses`,
    );
  }

  activateProcedure(
    procedureId: string,
    correlationId: string,
    rowVersion?: string,
  ): Observable<{
    newStatusCode: ProcedureStatusDisplayKey;
    activatedOn: string;
  }> {
    return this.http.post<{
      newStatusCode: ProcedureStatusDisplayKey;
      activatedOn: string;
    }>(`${this.baseUrl}/CMDISOProcedure/${procedureId}/activate`, {
      correlationId,
      rowVersion,
    });
  }

  deActivateProcedure(
    procedureId: string,
    correlationId: string,
    rowVersion?: string,
  ): Observable<{
    newStatusCode: ProcedureStatusDisplayKey;
    deactivatedOn: string;
  }> {
    return this.http.post<{
      newStatusCode: ProcedureStatusDisplayKey;
      deactivatedOn: string;
    }>(`${this.baseUrl}/CMDISOProcedure/${procedureId}/deactivate`, {
      correlationId,
      rowVersion,
    });
  }

  fetchProcedureDetails(procedureId: string): Observable<ProcedureDetailsDto> {
    return this.http
      .get<ProcedureDetailsDto>(
        `${this.baseUrl}/CMDISOProcedure/${procedureId}/procedureDetails`,
      )
      .pipe(
        map((procedureDetails) => {
          return {
            ...procedureDetails,
            actions: procedureDetails.actions?.map((action) => ({
              ...action,
            })),
          };
        }),
      );
  }

  fetchActionDetails(
    activeProcedureItemId: string,
  ): Observable<ActionDetailsDto> {
    return this.http.get<ActionDetailsDto>(
      `${this.baseUrl}/CMDISOProcedure/${activeProcedureItemId}/actionDetails`,
    );
  }

  quickApproval(
    procedureId: string,
    scope: QuickApprovalScope,
    policyId?: string,
  ): Observable<QuickApprovalResponse> {
    return this.http.post<QuickApprovalResponse>(
      `${this.baseUrl}/CMDISOProcedure/${procedureId}/items/quickapprove`,
      {
        scope,
        policyId,
      },
    );
  }
}
